import { API_URL, getAuthHeaders } from './config';

export const refereeService = {
    createProfile: async (data: any) => {
        const res = await fetch(`${API_URL}/referee/onboarding`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to create profile");
        return res.json();
    },
    getMyProfile: async () => {
        const res = await fetch(`${API_URL}/referee/me`, {
            headers: getAuthHeaders()
        });
        if (res.status === 404) return null; // Handle not found gracefully
        if (!res.ok) throw new Error("Failed to fetch profile");
        return res.json();
    },
    getAssignments: async () => {
        // Return Tournaments I am working on
        const res = await fetch(`${API_URL}/referee/assignments`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed");
        return res.json();
    },

    getMyMatches: async () => {
        // Return Matches I am referee of
        const res = await fetch(`${API_URL}/referee/matches`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch matches");
        return res.json();
    },

    // Search Referees (as Manager)
    search: async (lat: number, lon: number, radius: number = 50, country?: string, level?: string, name?: string) => {
        const query = new URLSearchParams({
            lat: lat.toString(),
            lon: lon.toString(),
            radius: radius.toString()
        });
        if (country) query.append('country', country);
        if (level) query.append('level', level);
        if (name) query.append('name', name);

        const res = await fetch(`${API_URL}/referee/search?${query}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Search failed");
        return res.json();
    },
    submitReview: async (payload: { match_id: number, referee_id: string, rating: number, traits: string[], comment?: string }) => {
        const res = await fetch(`${API_URL}/referee/review`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error("Failed to submit review");
        return res.json();
    },
    getStats: async (refereeId: string) => {
        const res = await fetch(`${API_URL}/referee/stats?referee_id=${refereeId}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch stats");
        return res.json();
    },

    // Levels
    getLevels: async (options: { federationId?: string, country?: string } = {}) => {
        const query = new URLSearchParams();
        if (options.federationId) query.append('federation_id', options.federationId);
        if (options.country) query.append('country', options.country);

        const res = await fetch(`${API_URL}/referee/levels?${query}`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch levels");
        return res.json();
    },

    // Federation Management (Federation Admin)
    getFederationPendingRequests: async (federationId: string) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referees/pending`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch pending requests");
        return res.json();
    },

    getFederationHistory: async (federationId: string) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referees/history`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch referee history");
        return res.json();
    },

    getFederationLevels: async (federationId: string) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referee-levels`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch federation referee levels");
        return res.json();
    },

    createFederationLevel: async (federationId: string, data: any) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referee-levels`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to create level");
        return res.json();
    },

    updateFederationLevel: async (federationId: string, levelId: string, data: any) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referee-levels/${levelId}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update level");
        return res.json();
    },

    deleteFederationLevel: async (federationId: string, levelId: string) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referee-levels/${levelId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to delete level");
        return res.json();
    },
    validateReferee: async (refereeUserId: string, status: 'verified' | 'rejected', rejection_reason?: string | null) => {
        const res = await fetch(`${API_URL}/referee/validate`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ referee_user_id: refereeUserId, status, rejection_reason })
        });
        if (!res.ok) throw new Error("Failed to validate referee");
        return res.json();
    },
    getProspection: async (federationId: string) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referees/prospection`, {
            headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error("Failed to fetch prospection data");
        return res.json();
    },
    affiliateToFederation: async (federationId: string, refereeUserId: string, data: any = {}) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referees/${refereeUserId}/affiliate`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to affiliate referee");
        return res.json();
    },
    rejectProspection: async (federationId: string, refereeUserId: string, reason: string) => {
        const res = await fetch(`${API_URL}/federations/${federationId}/referees/${refereeUserId}/reject-prospection`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ reason })
        });
        if (!res.ok) throw new Error("Failed to reject prospection");
        return res.json();
    }
};

